import { useState } from "react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Image as ImageIcon, FileText, Receipt, Sparkles, Plus, Paperclip, BookOpen, GripVertical } from "lucide-react";
import { Page, Attachment } from "@/lib/types";
import { sectionToPage } from "./ContentTab";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ICON: Record<string, any> = { cover: ImageIcon, content: FileText, before_after: Sparkles, pricing: Receipt };

interface Props {
  quote: { pages: Page[]; attachments: Attachment[] }; selected: string; onSelect: (id: string) => void; editable: boolean;
  onPagesChange: (pages: Page[]) => void; onAttachmentsChange?: (atts: Attachment[]) => void; sections: any[]; showAttachments?: boolean;
}

function SortableRow({ id, disabled, children, testid, active, onClick, indent }: any) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={testid} onClick={onClick}
      className={`group flex cursor-pointer items-center gap-1.5 rounded-md py-2 pr-2 text-sm transition-colors ${indent ? "ml-6 pl-1 text-xs" : "pl-1"} ${active ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"} ${isDragging ? "z-10 bg-white shadow-lg ring-1 ring-blue-200" : ""}`}>
      {!disabled && <button {...attributes} {...listeners} data-testid={`${testid}-drag-handle`} className="cursor-grab touch-none rounded p-0.5 text-gray-300 hover:text-gray-600 active:cursor-grabbing" onClick={(e) => e.stopPropagation()} aria-label="Drag to reorder"><GripVertical size={14} /></button>}
      {disabled && <span className="w-[19px]" />}
      {children}
    </div>
  );
}

export function LeftPanel({ quote, selected, onSelect, editable, onPagesChange, onAttachmentsChange, sections, showAttachments = true }: Props) {
  const [libOpen, setLibOpen] = useState(false);
  const pages = quote.pages;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const sectionAtts = quote.attachments.filter((a) => a.mode === "section").sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const appendices = quote.attachments.filter((a) => a.mode !== "section").sort((a, b) => a.sort_order - b.sort_order);

  const onPageDragEnd = (e: DragEndEvent) => {
    const { active, over } = e; if (!over || active.id === over.id) return;
    const from = pages.findIndex((p) => p.id === active.id), to = pages.findIndex((p) => p.id === over.id);
    const moved = arrayMove(pages, from, to);
    const remap = (pos: number) => moved.findIndex((p) => p.id === pages[pos]?.id);
    onPagesChange(moved);
    if (onAttachmentsChange && sectionAtts.length) onAttachmentsChange(quote.attachments.map((a) => a.mode === "section" ? { ...a, position: Math.max(0, remap(a.position ?? pages.length - 1)) } : a));
  };
  const onAppendixDragEnd = (e: DragEndEvent) => {
    const { active, over } = e; if (!over || active.id === over.id || !onAttachmentsChange) return;
    const moved = arrayMove(appendices, appendices.findIndex((a) => a.id === active.id), appendices.findIndex((a) => a.id === over.id)).map((a, i) => ({ ...a, sort_order: i }));
    onAttachmentsChange([...quote.attachments.filter((a) => a.mode === "section"), ...moved]);
  };

  const addPage = (p: Page) => { onPagesChange([...pages, p]); onSelect(p.id); setLibOpen(false); };
  const addBlank = () => addPage({ id: crypto.randomUUID(), type: "content", title: "New Section", subtitle: "", body: "", bullets: [], layout: "text_only" });
  const addFromLibrary = (s: any) => addPage(sectionToPage(s));
  const addBeforeAfter = () => addPage({ id: crypto.randomUUID(), type: "before_after", title: "Your Home, Illuminated", subtitle: "A preview of your custom design", body: "", bullets: [], layout: "stacked" });

  return (
    <aside className="flex h-full flex-col overflow-hidden border-r border-gray-200 bg-white" data-testid="left-panel">
      <div className="flex items-center justify-between px-4 py-3"><span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pages</span><span className="text-xs text-gray-400">{editable ? "drag to reorder" : pages.length}</span></div>
      <div className="scrollbar-thin flex-1 overflow-y-auto px-2 pb-4">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onPageDragEnd}>
          <SortableContext items={pages.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            {pages.map((p, i) => {
              const Icon = ICON[p.type] || FileText;
              return (
                <div key={p.id}>
                  <SortableRow id={p.id} disabled={!editable} testid={`page-item-${p.id}`} active={selected === p.id} onClick={() => onSelect(p.id)}>
                    <span className="w-4 text-right text-[10px] text-gray-400">{i + 1}</span>
                    <Icon size={15} className={selected === p.id ? "text-blue-700" : "text-gray-400"} />
                    <span className="min-w-0 flex-1 truncate">{p.title || p.type}</span>
                  </SortableRow>
                  {showAttachments && sectionAtts.filter((a) => (a.position ?? pages.length - 1) === i).map((a) => (
                    <div key={a.id} data-testid={`attachment-item-${a.id}`} onClick={() => onSelect(`att:${a.id}`)} className={`ml-6 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors ${selected === `att:${a.id}` ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-50"}`}>
                      <Paperclip size={12} className="text-gray-400" /><span className="truncate">{a.title}</span><span className="ml-auto rounded bg-gray-100 px-1 text-[10px]">PDF</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </SortableContext>
        </DndContext>
        {editable && <div className="mt-2 space-y-1 px-2">
          <Button variant="outline" size="sm" className="w-full justify-start text-xs" data-testid="add-library-page-btn" onClick={() => setLibOpen(true)}><BookOpen size={13} className="mr-1.5" />Insert from content library</Button>
          <Button variant="ghost" size="sm" className="w-full justify-start text-xs text-gray-600" data-testid="add-blank-page-btn" onClick={addBlank}><Plus size={13} className="mr-1.5" />Blank page</Button>
          <Button variant="ghost" size="sm" className="w-full justify-start text-xs text-gray-600" data-testid="add-before-after-page-btn" onClick={addBeforeAfter}><Sparkles size={13} className="mr-1.5" />Before / after spread</Button>
        </div>}
        {showAttachments && <>
          <div className="mt-5 flex items-center justify-between px-2 py-2"><span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Appendices</span><span className="text-xs text-gray-400">{appendices.length}</span></div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onAppendixDragEnd}>
            <SortableContext items={appendices.map((a) => a.id)} strategy={verticalListSortingStrategy}>
              {appendices.map((a, n) => (
                <SortableRow key={a.id} id={a.id} disabled={!editable} testid={`appendix-item-${a.id}`} active={selected === `att:${a.id}`} onClick={() => onSelect(`att:${a.id}`)}>
                  <span className="w-4 text-right text-[10px] text-gray-400">{String.fromCharCode(65 + n)}</span><Paperclip size={15} className="text-gray-400" /><span className="truncate">{a.title}</span>
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
          {appendices.length === 0 && <p className="px-2 text-xs text-gray-400">Import PDFs (e.g. Satellite Measurements) in the Files tab.</p>}
        </>}
      </div>

      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="library-dialog">
          <DialogHeader><DialogTitle>Starlites Content Library</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {sections.map((s) => (
              <button key={s.id} data-testid={`library-section-${s.id}`} onClick={() => addFromLibrary(s)} className="rounded-lg border border-gray-200 p-4 text-left transition-colors hover:border-blue-400 hover:bg-blue-50">
                <div className="text-[10px] uppercase tracking-wider text-gray-400">{s.source?.replace("_", " ")} · {s.kind}</div>
                <div className="mt-1 font-semibold text-gray-900">{s.title}</div>
                <div className="mt-1 line-clamp-2 text-xs text-gray-500">{s.body}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
